/* BusinessHours — Firm Branding > Business Hours tab
   Key: business_hours (JSON string)
   Schema: { mon: { enabled: boolean; open: string; close: string }, … }
   No new migration needed — key seeded in Phase 1. */
import { useEffect, useState } from 'react';
import css from '../../../AdminPage.module.css';
import { useFirmBranding, useUpdateFirmBranding } from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface DayConfig {
  enabled: boolean;
  open: string; // HH:MM
  close: string; // HH:MM
}

export type WeekSchedule = {
  mon: DayConfig;
  tue: DayConfig;
  wed: DayConfig;
  thu: DayConfig;
  fri: DayConfig;
  sat: DayConfig;
  sun: DayConfig;
};

export const DAYS: { key: keyof WeekSchedule; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

export const DEFAULT_SCHEDULE: WeekSchedule = {
  mon: { enabled: true, open: '09:00', close: '17:30' },
  tue: { enabled: true, open: '09:00', close: '17:30' },
  wed: { enabled: true, open: '09:00', close: '17:30' },
  thu: { enabled: true, open: '09:00', close: '17:30' },
  fri: { enabled: true, open: '09:00', close: '17:30' },
  sat: { enabled: false, open: '09:00', close: '13:00' },
  sun: { enabled: false, open: '09:00', close: '13:00' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function parseSchedule(json: string | null | undefined): WeekSchedule {
  if (!json) return DEFAULT_SCHEDULE;
  try {
    return { ...DEFAULT_SCHEDULE, ...(JSON.parse(json) as Partial<WeekSchedule>) };
  } catch {
    return DEFAULT_SCHEDULE;
  }
}

function scheduleEqual(a: WeekSchedule, b: WeekSchedule): boolean {
  return (Object.keys(a) as (keyof WeekSchedule)[]).every(
    (d) => a[d].enabled === b[d].enabled && a[d].open === b[d].open && a[d].close === b[d].close,
  );
}

/** Returns an error message if any enabled day has close ≤ open, otherwise null. */
function validateSchedule(schedule: WeekSchedule): string | null {
  for (const { key, label } of DAYS) {
    const day = schedule[key];
    if (day.enabled && day.close <= day.open) {
      return `${label}: closing time must be after opening time`;
    }
  }
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BusinessHours() {
  const { data: branding, isLoading } = useFirmBranding();
  const update = useUpdateFirmBranding();
  const { showToast } = useToast();

  const [draft, setDraft] = useState<WeekSchedule>(DEFAULT_SCHEDULE);
  const [original, setOriginal] = useState<WeekSchedule>(DEFAULT_SCHEDULE);

  useEffect(() => {
    if (!branding) return;
    const parsed = parseSchedule(branding.business_hours);
    setDraft(parsed);
    setOriginal(parsed);
  }, [branding]);

  function setDay(day: keyof WeekSchedule, patch: Partial<DayConfig>) {
    setDraft((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
  }

  async function handleSave() {
    const validationError = validateSchedule(draft);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }
    try {
      await update.mutateAsync([{ key: 'business_hours', value: JSON.stringify(draft) }]);
      setOriginal(draft);
      showToast('Business hours saved', 'success');
    } catch {
      showToast('Failed to save business hours', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
        <span className={css.spinnerDot} />
      </div>
    );
  }

  const dirty = !scheduleEqual(draft, original);

  return (
    <div>
      <p style={{ marginBottom: 20, color: 'var(--text-secondary)' }}>
        Set your firm's operational hours. These are used for SLA calculations and to indicate
        availability to clients.
      </p>

      {/* ── Schedule grid ── */}
      <div data-testid="schedule-grid" style={{ maxWidth: 560, marginBottom: 28 }}>
        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '110px 48px 90px 16px 90px',
            gap: '0 10px',
            marginBottom: 6,
            paddingBottom: 6,
            borderBottom: '1px solid var(--border-primary)',
          }}
        >
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            Day
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-muted)',
              textAlign: 'center',
            }}
          >
            Open
          </span>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            From
          </span>
          <span />
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>
            To
          </span>
        </div>

        {DAYS.map(({ key, label }) => {
          const day = draft[key];
          return (
            <div
              key={key}
              data-testid={`row-${key}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 48px 90px 16px 90px',
                gap: '0 10px',
                alignItems: 'center',
                marginBottom: 8,
                opacity: day.enabled ? 1 : 0.45,
              }}
            >
              {/* Day label */}
              <label
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
                htmlFor={`toggle-${key}`}
              >
                {label}
              </label>

              {/* Enabled toggle */}
              <input
                id={`toggle-${key}`}
                data-testid={`toggle-${key}`}
                type="checkbox"
                checked={day.enabled}
                onChange={(e) => setDay(key, { enabled: e.target.checked })}
                style={{ width: 16, height: 16, cursor: 'pointer', justifySelf: 'center' }}
              />

              {/* Open time */}
              <input
                data-testid={`open-${key}`}
                type="time"
                value={day.open}
                disabled={!day.enabled}
                onChange={(e) => setDay(key, { open: e.target.value })}
                className={css.formInput}
                style={{ padding: '4px 6px', fontSize: '0.8125rem' }}
              />

              <span
                style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.75rem' }}
              >
                –
              </span>

              {/* Close time */}
              <input
                data-testid={`close-${key}`}
                type="time"
                value={day.close}
                disabled={!day.enabled}
                onChange={(e) => setDay(key, { close: e.target.value })}
                className={css.formInput}
                style={{ padding: '4px 6px', fontSize: '0.8125rem' }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Quick-select presets ── */}
      <div style={{ marginBottom: 24, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          data-testid="preset-weekdays"
          className={css.btnSmall}
          type="button"
          onClick={() =>
            setDraft((prev) => {
              const next = { ...prev };
              (['mon', 'tue', 'wed', 'thu', 'fri'] as const).forEach(
                (d) => (next[d] = { ...next[d], enabled: true }),
              );
              (['sat', 'sun'] as const).forEach((d) => (next[d] = { ...next[d], enabled: false }));
              return next;
            })
          }
        >
          Mon–Fri only
        </button>
        <button
          data-testid="preset-all"
          className={css.btnSmall}
          type="button"
          onClick={() =>
            setDraft((prev) => {
              const next = { ...prev };
              DAYS.forEach(({ key: d }) => (next[d] = { ...next[d], enabled: true }));
              return next;
            })
          }
        >
          All 7 days
        </button>
      </div>

      <button
        className={css.btnPrimary}
        data-testid="btn-save"
        onClick={handleSave}
        disabled={!dirty || update.isPending}
      >
        {update.isPending ? 'Saving…' : 'Save Business Hours'}
      </button>
    </div>
  );
}
